const DAY_FORMAT = { month: "short", day: "numeric" };
const TIME_FORMAT = { hour: "2-digit", minute: "2-digit" };

const toDate = (value) => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const formatDuration = (minutes) => {
  if (!Number.isFinite(minutes) || minutes <= 0) return "0m";
  const hours = Math.floor(minutes / 60);
  const mins = Math.floor(minutes % 60);
  return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
};

export const formatCost = (amount) =>
  Number.isFinite(amount) ? `$${Math.ceil(amount).toLocaleString("en-US")}` : "N/A";

export const formatDay = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleDateString("en-US", DAY_FORMAT) : "—";
};

export const formatTime = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleTimeString("en-US", TIME_FORMAT) : "";
};

// A drill-down can list meetings that belong to other people — My Organization
// is entirely their meetings. Those events are not in this user's
// calendarEvents, so the Workspace would open on nothing.
export const isMeetingOnUserCalendar = (meeting, userEmail) => {
  // Unknown viewer means the user store has not hydrated yet. Let the click
  // through rather than blocking a meeting that may well be theirs.
  if (!userEmail) return true;

  const email = userEmail.toLowerCase();

  if (meeting?.organizer?.email?.toLowerCase() === email) return true;

  // Match on the address only. Google's `self` flag is set relative to the
  // calendar the event was read from — the organizer's, for these meetings —
  // so it is true for every viewer and cannot say whose calendar this is.
  //
  // Declined attendees still keep the event on their calendar, so membership
  // is enough here — no need to look at responseStatus.
  return Boolean(
    meeting?.attendees?.some(
      (attendee) => attendee?.email?.toLowerCase() === email
    )
  );
};

// Same rule as the meeting page's goal pill: the organizer, or an org admin
// when the organizer is internal. Emails are compared, never Google's
// `organizer.self` — these rows were harvested from other people's calendars,
// where that flag says nothing about the viewer. The meeting must also be on
// the viewer's own calendar, because the backend reads their copy of it.
export const canEditMeetingGoal = (meeting, user) => {
  const userEmail = user?.email?.toLowerCase();
  const organizerEmail = meeting?.organizer?.email?.toLowerCase();
  if (!userEmail || !organizerEmail) return false;
  if (!isMeetingOnUserCalendar(meeting, userEmail)) return false;

  if (organizerEmail === userEmail) return true;

  const isAdmin = Boolean(user?.roles?.includes("admin"));
  const sameDomain = organizerEmail.split("@")[1] === userEmail.split("@")[1];
  return isAdmin && sameDomain;
};

export const getAttendeeEmails = (attendees) =>
  (Array.isArray(attendees) ? attendees : [])
    .map((attendee) => attendee?.email)
    .filter(Boolean);

export const getMeetingDuration = (meeting) => {
  const start = toDate(meeting?.start?.dateTime);
  const end = toDate(meeting?.end?.dateTime);
  if (!start || !end) return 0;
  return Math.max(0, Math.round((end - start) / 60000));
};

// Estimated costs are already rendered as "$", "$$", "$$$" by the backend,
// so they must not be prefixed with another dollar sign.
export const getMeetingCostLabel = (costInfo) => {
  if (costInfo?.estimated) return costInfo?.dollarSign || "N/A";
  return Number.isFinite(costInfo?.amount) ? formatCost(costInfo.amount) : "N/A";
};

export const sortMeetingsByStart = (meetingDetails) =>
  [...(meetingDetails || [])].sort(
    (a, b) => new Date(b.start?.dateTime) - new Date(a.start?.dateTime)
  );

export const calculateInsights = (meetingDetails) => {
  if (!meetingDetails || meetingDetails.length === 0) {
    return {
      totalMeetings: 0,
      totalDuration: 0,
      averageDuration: 0,
      averageCost: 0,
    };
  }

  const totalMeetings = meetingDetails.length;
  let totalDuration = 0;
  let totalCost = 0;
  let costCount = 0;

  meetingDetails.forEach((meeting) => {
    totalDuration += getMeetingDuration(meeting);

    if (meeting.costInfo?.amount) {
      totalCost += meeting.costInfo.amount;
      costCount++;
    }
  });

  return {
    totalMeetings,
    totalDuration,
    averageDuration: Math.round(totalDuration / totalMeetings),
    averageCost: costCount > 0 ? Math.ceil(totalCost / costCount) : 0,
  };
};
